import './../_addresses.scss'

import * as variable from 'utils/Variable'
import * as helper from 'utils/HelperFunction'

import { Box, Grid, Typography } from '@mui/material'
import { ChangeEvent, useEffect, useState } from 'react'

import TextfieldCurrencyBaseline from 'components/Textfield/Baseline/TextfieldCurrencyBaseline'
import SelectReferenceData from 'components/Select/Baseline/SelectReferenceData'
import { List } from 'react-content-loader'
import { NumericFormat } from 'react-number-format'
import {
  residentialStatusRequired,
  monthlyMortgagePayment,
  monthlyRentPayment,
  monthlyPaymentRequired,
} from '../../../../../utils/Messages'
import UseAccountApiRequest from '../../../../portal/accountDetails/UseAccountApiRequest'
import useApiRequest from '../currentAddress/UseApiRequest'

type ErrorsInput = {
  residentialStatus: boolean
  monthlyPayment: boolean
}

type Props = {
  parentCallbackMonthlyPayment: (monthlyAmount: string) => void
  parentCallbackResidentialStatus: (residentialStatus: string) => void
  errors: ErrorsInput
  setErrors: (errors: ErrorsInput) => void
}

const Addresses = ({
  parentCallbackMonthlyPayment,
  parentCallbackResidentialStatus,
  errors,
  setErrors,
}: Props): JSX.Element => {
  const { accountDetails, fetchAccountDetails } = UseAccountApiRequest()
  const [residentialStatus, setResidentialStatus] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [hasLoaded, setHasLoaded] = useState(true)
  const [monthlyPayment, setMonthlyPayment] = useState<string>('')

  const props = useApiRequest()

  useEffect(() => {
    if (hasLoaded) {
      fetchAccountDetails(setLoading)
      setHasLoaded(false)
    }
  }, [hasLoaded, accountDetails, fetchAccountDetails])

  useEffect(() => {
    const formResidentialStatus = accountDetails?.residentialStatus ? accountDetails?.residentialStatus : ''
    setResidentialStatus(formResidentialStatus)
    parentCallbackResidentialStatus(formResidentialStatus)
  }, [accountDetails, parentCallbackResidentialStatus])

  useEffect(() => {
    if (loading) {
      props.fetchResidentialStatuses()
    }
    setLoading(false)
  }, [loading, setLoading, props.fetchResidentialStatuses, props])

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    setMonthlyPayment(value)
    parentCallbackMonthlyPayment(value)

    setErrors({
      ...errors,
      monthlyPayment: Number.parseInt(value) === 0 || value === '',
    })
  }

  const handleResidentialStatusesOption = (chosenStatus: string) => {
    setResidentialStatus(chosenStatus)
    parentCallbackResidentialStatus(chosenStatus)

    setErrors({
      ...errors,
      residentialStatus: chosenStatus === '',
    })
  }

  if (loading) {
    return <List />
  }

  return (
    <Grid container className="addresses-container" data-test="addresses-container">
      <Grid item xs={12}>
        <Box>
          <Box mb={3} className="Card-shadow Headers lines first-address-container">
            <Grid item xs={12} className="Account-content line-separate">
              <Box>
                <Typography variant="h4">2. Address details</Typography>
              </Box>
            </Grid>

            <Box mt={3} mb={3} className="blocklevel address-details-container">
              <span className="subHeader">Current address</span>
              {helper.checkIsEmptyObject(accountDetails.address) && (
                <>
                  {helper.stringIsDefinedAndNotEmpty(accountDetails?.address?.line1) && (
                    <span className="paragraph">{accountDetails?.address?.line1}</span>
                  )}
                  {helper.stringIsDefinedAndNotEmpty(accountDetails?.address?.line2) && (
                    <span className="paragraph">{accountDetails?.address?.line2}</span>
                  )}
                  {helper.stringIsDefinedAndNotEmpty(accountDetails?.address?.line3) && (
                    <span className="paragraph">{accountDetails?.address?.line3}</span>
                  )}
                  {helper.stringIsDefinedAndNotEmpty(accountDetails?.address?.line4) && (
                    <span className="paragraph">{accountDetails?.address?.line4}</span>
                  )}
                  {helper.stringIsDefinedAndNotEmpty(accountDetails?.address?.postcode) && (
                    <span className="paragraph">{accountDetails?.address?.postcode}</span>
                  )}
                </>
              )}
            </Box>

            <Box mt={3} mb={3} className="blocklevel address-details-container">
              <SelectReferenceData
                dataTest="residential-status"
                variant="filled"
                onChange={handleResidentialStatusesOption}
                value={residentialStatus}
                options={props.residentialStatuses}
                label="Residential status"
                error={errors.residentialStatus}
                helperText={errors.residentialStatus ? residentialStatusRequired : ''}
              />

              <Box mt={3}>
                {variable.residentialStatusNeedMonthlyPayment.includes(residentialStatus) && (
                  <Box mb={2} data-test="monthly-mortgage-or-rent-payment">
                    <NumericFormat
                      id="homeowner-with-mortgage"
                      decimalScale={2}
                      thousandSeparator={','}
                      decimalSeparator={'.'}
                      fullWidth
                      customInput={TextfieldCurrencyBaseline}
                      label={
                        residentialStatus === 'Homeowner - With Mortgage' ? monthlyMortgagePayment : monthlyRentPayment
                      }
                      value={monthlyPayment}
                      onChange={handleAmountChange}
                      error={errors.monthlyPayment}
                      helperText={errors.monthlyPayment ? monthlyPaymentRequired : null}
                    />
                  </Box>
                )}
              </Box>

              <Box mt={3} className="paperInstruction" style={{ display: 'flex' }}>
                <Box>
                  <span className="lw-marketing-icons-light-bulb-on icon-bg-notice icon-bold"></span>
                </Box>
                <Box data-test="paper-instruction">If your details above have changed, please contact us.</Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default Addresses
